import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardPagination from '../components/DashboardPagination';
import emendaService, {
  isEmendaElegivelParaNovoPlano,
  resolveEmendaStatus,
  type EmendaDTO,
} from '../services/emendaService';
import planoService from '../services/planoTrabalhoService';
import { UI_THEME } from '../ui/theme';
import Button from '../components/ui/Button';
import { FIELD_CLASS } from '../components/ui/fieldStyles';
import { Table, Tbody, Td, Th, Thead, Tr } from '../components/ui/Table';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const secretariaDaEmenda = (emenda: EmendaDTO): string =>
  emenda.institutionName?.trim()
  || emenda.secretariaDestino?.trim()
  || 'Secretaria não informada';

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { error?: string } } }).response;
    if (response?.data?.error) return response.data.error;
  }
  return 'Não foi possível carregar o relatório de secretarias sem plano.';
};

type RelatorioSecretariasSemPlanoPageProps = {
  embedded?: boolean;
};

const RelatorioSecretariasSemPlanoPage: React.FC<RelatorioSecretariasSemPlanoPageProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [emendas, setEmendas] = useState<EmendaDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [secretaria, setSecretaria] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const isSaas = UI_THEME === 'saas';
  const accentClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [todasEmendas, planos] = await Promise.all([
          emendaService.listWithDetails(),
          planoService.listAll({ minhaFila: false }),
        ]);
        if (!active) return;

        const emendaIdsComPlano = new Set(
          planos.map(plano => plano.emendaId).filter((id): id is string => Boolean(id)),
        );
        const pendentes = todasEmendas.filter(emenda =>
          Boolean(emenda.id)
          && String(emenda.tipoTransferencia || '').trim().toLocaleLowerCase('pt-BR') === 'direta'
          && isEmendaElegivelParaNovoPlano(emenda)
          && !emendaIdsComPlano.has(emenda.id!),
        );
        setEmendas(pendentes);
      } catch (err: unknown) {
        if (!active) return;
        setError(getErrorMessage(err));
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, []);

  const secretarias = useMemo(() =>
    Array.from(new Set(emendas.map(secretariaDaEmenda))).sort((a, b) => a.localeCompare(b, 'pt-BR')),
  [emendas]);

  const filtradas = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('pt-BR');
    return emendas
      .filter(emenda => !secretaria || secretariaDaEmenda(emenda) === secretaria)
      .filter(emenda => {
        if (!query) return true;
        return [
          emenda.officialCode,
          emenda.numeroEmenda,
          emenda.exercicio,
          emenda.councilorName,
          emenda.description,
          emenda.objectDetail,
          secretariaDaEmenda(emenda),
        ].some(value => String(value || '').toLocaleLowerCase('pt-BR').includes(query));
      })
      .sort((a, b) => {
        const porSecretaria = secretariaDaEmenda(a).localeCompare(secretariaDaEmenda(b), 'pt-BR');
        if (porSecretaria !== 0) return porSecretaria;
        return String(a.officialCode || a.id).localeCompare(String(b.officialCode || b.id), 'pt-BR');
      });
  }, [emendas, search, secretaria]);

  const resumo = useMemo(() => {
    const grupos = new Map<string, { secretaria: string; quantidade: number; valor: number }>();
    filtradas.forEach(emenda => {
      const nome = secretariaDaEmenda(emenda);
      const atual = grupos.get(nome) || { secretaria: nome, quantidade: 0, valor: 0 };
      atual.quantidade += 1;
      atual.valor += Number(emenda.value || 0);
      grupos.set(nome, atual);
    });
    return Array.from(grupos.values()).sort((a, b) => a.secretaria.localeCompare(b.secretaria, 'pt-BR'));
  }, [filtradas]);

  useEffect(() => {
    setPage(1);
  }, [search, secretaria, pageSize]);

  const totalValor = filtradas.reduce((total, emenda) => total + Number(emenda.value || 0), 0);
  const inicio = (page - 1) * pageSize;
  const paginaAtual = filtradas.slice(inicio, inicio + pageSize);

  return (
    <div className="w-full space-y-6">
      {!embedded && <header>
        <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentClass}`}>Relatórios operacionais</p>
        <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">Secretarias sem plano de trabalho</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Emendas diretas com análise de demanda aprovada que ainda não possuem plano de trabalho lançado.
        </p>
      </header>}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-5 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}

      {!error && (
        <>
          <section className="grid overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900 sm:grid-cols-3">
            {[
              { label: 'Secretarias pendentes', value: resumo.length, dot: isSaas ? 'bg-teal-500' : 'bg-blue-600' },
              { label: 'Emendas sem plano', value: filtradas.length, dot: 'bg-amber-500' },
              { label: 'Valor aguardando plano', value: currencyFormatter.format(totalValor), dot: 'bg-emerald-500' },
            ].map((item, index) => (
              <div key={item.label} className={`px-5 py-4 ${index > 0 ? 'border-t border-slate-200 dark:border-slate-700 sm:border-l sm:border-t-0' : ''}`}>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <span className={`h-2 w-2 rounded-full ${item.dot}`} />
                  {item.label}
                </div>
                <div className="mt-1 text-xl font-bold text-slate-900 dark:text-white">{item.value}</div>
              </div>
            ))}
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(260px,0.45fr)_auto]">
              <input
                type="search"
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Buscar por emenda, secretaria, parlamentar ou objeto..."
                className={FIELD_CLASS}
              />
              <select
                value={secretaria}
                onChange={event => setSecretaria(event.target.value)}
                className={FIELD_CLASS}
              >
                <option value="">Todas as secretarias</option>
                {secretarias.map(nome => <option key={nome} value={nome}>{nome}</option>)}
              </select>
              <Button variant="secondary" onClick={() => { setSearch(''); setSecretaria(''); }}>
                Limpar filtros
              </Button>
            </div>
          </section>

          {loading ? (
            <div className="rounded-2xl border border-slate-200 px-4 py-12 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
              Carregando relatório...
            </div>
          ) : filtradas.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-12 text-center dark:border-slate-700 dark:bg-slate-900">
              <p className="font-medium text-slate-700 dark:text-slate-200">Nenhuma secretaria possui emenda aprovada aguardando plano.</p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Todas as emendas elegíveis já possuem plano de trabalho lançado.</p>
            </div>
          ) : (
            <>
              <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
                  <h2 className="font-semibold text-slate-900 dark:text-white">Resumo por secretaria</h2>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>Secretaria</Th>
                        <Th align="right">Emendas sem plano</Th>
                        <Th align="right">Valor</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {resumo.map(item => (
                        <Tr key={item.secretaria} className="border-t border-slate-100 dark:border-slate-800">
                          <Td className="text-sm font-medium text-slate-800 dark:text-slate-100">{item.secretaria}</Td>
                          <Td align="right" className="text-sm text-slate-700 dark:text-slate-200">{item.quantidade}</Td>
                          <Td align="right" className="text-sm font-medium text-slate-800 dark:text-slate-100">{currencyFormatter.format(item.valor)}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </div>
              </section>

              <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
                  <h2 className="font-semibold text-slate-900 dark:text-white">Emendas aguardando plano</h2>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>Secretaria</Th>
                        <Th>Emenda</Th>
                        <Th>Parlamentar / Objeto</Th>
                        <Th>Etapa</Th>
                        <Th align="right">Valor</Th>
                        <Th align="right">Abrir</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {paginaAtual.map(emenda => (
                        <Tr key={emenda.id} className="border-t border-slate-100 dark:border-slate-800">
                          <Td className="text-sm font-medium text-slate-800 dark:text-slate-100">{secretariaDaEmenda(emenda)}</Td>
                          <Td className="whitespace-nowrap text-sm text-slate-700 dark:text-slate-200">
                            <div className="font-semibold">{emenda.officialCode || emenda.id}</div>
                            {emenda.exercicio && <div className="text-xs text-slate-500 dark:text-slate-400">Exercício {emenda.exercicio}</div>}
                          </Td>
                          <Td className="max-w-md text-sm text-slate-700 dark:text-slate-200">
                            <div>Parlamentar: {emenda.councilorName || 'Não informado'}</div>
                            <div className="mt-1 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">{emenda.objectDetail || emenda.description || 'Objeto não informado'}</div>
                          </Td>
                          <Td className="whitespace-nowrap text-sm text-slate-700 dark:text-slate-200">{resolveEmendaStatus(emenda)}</Td>
                          <Td align="right" className="whitespace-nowrap text-sm font-medium text-slate-800 dark:text-slate-100">{currencyFormatter.format(Number(emenda.value || 0))}</Td>
                          <Td align="right">
                            <button
                              type="button"
                              onClick={() => navigate(`/dashboard/emendas/${emenda.id}`)}
                              className={`text-sm font-semibold ${accentClass} hover:underline`}
                            >
                              Ver emenda
                            </button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </div>
                <DashboardPagination
                  page={page}
                  pageSize={pageSize}
                  totalItems={filtradas.length}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                  pageSizeOptions={[10, 20, 50]}
                  itemLabel="emendas"
                />
              </section>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default RelatorioSecretariasSemPlanoPage;
